import './App.css';
import 'react-toastify/dist/ReactToastify.css';

import { Suspense, useEffect, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Flip, ToastContainer } from 'react-toastify';
import styled from 'styled-components';

import AccountsUpdateComponent from './components/AccountsUpdateModal';
import AntiAdBlockerComponent from './components/AntiAdBlockerToast';
import FaqSection from './components/FaqSection';
import Footer from './components/Footer';
import Header from './components/Header';
import { IFrameWarningToast } from './components/IFrameWarningToast';
import { RemoveAdsButton } from './components/RemoveAdsButton';
import ScrollingBlackFridayPrompt from './components/ScrollingBlackFridayPrompt';
import { getDayString } from './hooks/useDailySeed';
import { useSettings } from './hooks/useSettings';
import { useUser } from './providers/UserProvider';
import AccountRoute from './routes/AccountRoute';
import ArchiveDateRoute from './routes/ArchiveDateRoute';
import ArchiveRoute from './routes/ArchiveRoute';
import { ChangeStatsRoute } from './routes/ChangeStatsRoute';
import { CityFlagsRoute } from './routes/CityFlagsRoute';
import { EmblemsRoute } from './routes/EmblemsRoute';
import GameRoute from './routes/GameRoute';
import PaymentReturnRoute from './routes/PaymentReturnRoute';
import RemoveAdsRoute from './routes/RemoveAdsRoute';
import ReplayRoute from './routes/ReplayRoute';
import RulesRoute from './routes/RulesRoute';
import { SettingsRoute } from './routes/SettingsRoute';
import { StatsRoute } from './routes/StatsRoute';
import { TeuteufGamesRoute } from './routes/TeuteufGamesRoute';
import { checkForConsent, detectAdBlocker, loadAdScript } from './utils/ads';
import { inIFrame } from './utils/inIFrame';

/**** MJD - ADDED THIS TO SUPPORT BONUS ROUND FIX *****/

const startDate = getDayString();

window &&
  window.setInterval(() => {
    const currentDate = getDayString();
    if (startDate !== currentDate) {
      window.dispatchEvent(new CustomEvent('date-changed'));
    }
  }, 1000);

const refreshPage = () => {
  window && (window.location.href = '/');
};

/**** MJD - ADDED THIS TO SUPPORT BONUS ROUND FIX END  *****/

export function App() {
  const [showIFrameWarning, setShowIFrameWarning] = useState<boolean>(false);
  const [settings] = useSettings();
  const { userDetails, isLoaded } = useUser();
  const [adBlockerDetected, setAdBlockerDetected] = useState<boolean>(false);

  useEffect(() => {
    checkForConsent();
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    const checkAdBlocker = async () => {
      setAdBlockerDetected(await detectAdBlocker());
    };

    loadAdScript(userDetails.premiumGames?.includes('flagle'));
    if (!userDetails.premiumGames?.includes('flagle')) {
      checkAdBlocker();
    }
  }, [isLoaded, userDetails.premiumGames]);

  /**** MJD - ADDED THIS TO SUPPORT BONUS ROUND FIX *****/
  useEffect(() => {
    window.addEventListener('date-changed', refreshPage);
    return () => {
      window.removeEventListener('date-changed', refreshPage);
    };
  }, []);
  /**** MJD - ADDED THIS TO SUPPORT BONUS ROUND FIX END *****/

  useEffect(() => {
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;
    if (
      settings.theme === 'dark' ||
      (typeof settings.theme === 'undefined' && prefersDark)
    ) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('bg-[#020617]');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('bg-[#9da2b6]');
    }
  }, [settings]);

  useEffect(() => {
    if (inIFrame()) {
      setShowIFrameWarning(true);
      window.gtag && window.gtag('event', 'iframe_popup');
    }
  }, []);

  return (
    <div className="App dark:text-white">
      <ToastContainer
        hideProgressBar
        position="top-center"
        transition={Flip}
        autoClose={false}
      />
      <IFrameWarningToast show={showIFrameWarning} />
      <AccountsUpdateComponent />
      <AntiAdBlockerComponent adBlockerDetected={adBlockerDetected} />
      <ScrollingBlackFridayPrompt />
      <div className="m-0 overflow-auto p-0 w-full h-full flex justify-center flex-row App-center lg:bg-[#f3f4f6] dark:bg-[#0f172a] lg:dark:bg-[#020617]">
        <div className="flex-1 flex justify-end">
          {!userDetails?.premiumGames?.includes('flagle') && (
            <AdWrapper className="snigel-sidev">
              <RemoveAdsButton className="mb-2 mt-2" />
              <div id="adngin-sidebar_left-0"></div>
              <div className="mt-[15px]"></div>
              <div id="adngin-sidebar_left_bottom-0"></div>
            </AdWrapper>
          )}
        </div>

        <div className="w-full flex h-full flex-col max-w-lg justify-start items-center relative pb-20 lg:pb-32 lg:box-content lg:px-12 lg:bg-white lg:dark:bg-slate-900 lg:shadow-2xl">
          <Header />
          <AppRouter />
          <Footer />
        </div>

        <div className="flex-1"></div>
      </div>
      {!userDetails?.premiumGames?.includes('flagle') && (
        <>
          <div id="adngin-video-0"></div>
          <div id="adngin-top_banner-0"></div>
        </>
      )}
    </div>
  );
}

const AppRouter = () => {
  return (
    <>
      <Suspense fallback="loading…">
        <Switch>
          <Route path="/archive/:date">
            <ArchiveDateRoute />
          </Route>
          <Route exact path="/">
            <GameRoute />
          </Route>
          <Route exact path="/logged-in"></Route>
          <Route exact path="/settings">
            <SettingsRoute />
          </Route>
          <Route exact path="/stats">
            <StatsRoute />
          </Route>
          <Route exact path="/rules">
            <RulesRoute />
          </Route>
          <Route exact path="/account">
            <AccountRoute />
          </Route>
          <Route exact path="/teuteuf-games">
            <TeuteufGamesRoute />
          </Route>
          <Route exact path="/archive">
            <ArchiveRoute />
          </Route>
          <Route exact path="/replay">
            <ReplayRoute />
          </Route>
          <Route exact path="/faq">
            <FaqSection />
          </Route>
          <Route exact path="/city-flags">
            <CityFlagsRoute />
          </Route>
          <Route exact path="/emblems">
            <EmblemsRoute />
          </Route>
          <Route exact path="/change-stats">
            <ChangeStatsRoute />
          </Route>
          <Route exact path="/removeads">
            <RemoveAdsRoute />
          </Route>
          <Route exact path="/paymentreturn">
            <PaymentReturnRoute />
          </Route>

          {/* legacy bonus round routes - redirect to game page */}
          <Redirect exact path="/bonus-round/1" to="/" />
          <Redirect exact path="/bonus-round/2" to="/" />
          <Redirect exact path="/bonus-round/3" to="/" />
          <Redirect exact path="/bonus-round/4" to="/" />
          <Redirect exact path="/bonus-round/5" to="/" />
        </Switch>
      </Suspense>
    </>
  );
};

const AdWrapper = styled('div')`
  position: sticky;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 40px 20px 20px;
  pointer-events: all;
`;
